import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { InputText } from "primereact/inputtext";
import { Skeleton } from "primereact/skeleton";
import type { ExportModalEntity } from "./types";
import { getCountryTabs } from "./getUniqueCountries";
import { filterEntitiesByCountry } from "./filterEntitiesByCountry";
import { EntityCard } from "./EntityCard";
import { DatasetChecklist } from "./DatasetChecklist";
import { hasEntityPlus } from "./hasEntityPlus";
import {
  buildDatasetTreeNodes,
  countSelectedLeaves,
  treeSelectionToDatasetKeys,
} from "./datasetTreeNodes";
import type { TreeSelectionKeys } from "./datasetTreeNodes";
import { filterAllowedDatasets } from "~/features/entity-export/filterAllowedDatasets";
import { useEntityExport } from "~/features/entity-export/useEntityExport";
import { DATASET_LABELS } from "~/features/entity-export/constants";
import type { ExportDatasetKey } from "~/features/entity-export/constants";
import CustomeButton from "~/components/Button";
import "./ExportEntityDataModal.scss";

const EXPORT_WARNING_TITLE = "This download contains confidential entity data.";

const EXPORT_WARNING_MESSAGE =
  "Unauthorized use, sharing, or storage may result in legal liability. Proceed only if you have the right to handle this information. Are you sure you want to export this data?";

const EXPORT_SUCCESS_TITLE = "Your download is ready!";

const EXPORT_SUCCESS_MESSAGE =
  "The file has been exported successfully. Check your downloads folder.";

const EXPORT_ERROR_MESSAGE =
  "We couldn't download your file. Check your connection and try again.";

/**
 * Props for the export entity data modal.
 *
 * @property visible - Controls dialog visibility
 * @property onClose - Called when the user dismisses the modal (cancel, backdrop, or post-download close)
 * @property entities - Full list of entities available for export. Each entity's `services`
 *   field determines whether Entity Plus datasets are shown in its checklist.
 * @property isLoading - When true, renders skeleton cards instead of entity list
 */
export interface ExportEntityDataModalProps {
  visible: boolean;
  onClose: () => void;
  entities: ExportModalEntity[];
  isLoading?: boolean;
}

/**
 * Modal dialog for exporting entity data as Excel.
 *
 * Flow: entity selection -> dataset checklist per entity -> confirm dialog -> download -> success dialog.
 *
 * Uses a two-dialog nested pattern: the main modal contains both a confirmation
 * dialog and a success dialog, shown sequentially during the export flow.
 * All internal state (selections, search, sub-dialogs) is reset when the
 * modal closes via the `visible` prop changing to false (GXM-1883).
 */
export function ExportEntityDataModal({
  visible,
  onClose,
  entities,
  isLoading = false,
}: ExportEntityDataModalProps): ReactElement {
  /** Active country tab filter - "ALL" shows every entity. */
  const [activeCountryValue, setActiveCountryValue] = useState<string>("ALL");
  /** Set of entity IDs the user has checked in the entity list. */
  const [selectedEntityIds, setSelectedEntityIds] = useState<Set<string>>(
    () => new Set(),
  );
  /** Per-entity dataset tree checkbox state, keyed by entity ID. */
  const [entityTreeSelections, setEntityTreeSelections] = useState<
    Record<string, TreeSelectionKeys>
  >({});
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [successDialogVisible, setSuccessDialogVisible] = useState(false);

  const { exportEntities, isExporting, error } = useEntityExport();

  const { tab: tabMode, tabs } = useMemo(
    () => getCountryTabs(entities),
    [entities],
  );

  const filteredEntities = useMemo(() => {
    let result = filterEntitiesByCountry(entities, activeCountryValue);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (e) =>
          e.name?.toLowerCase().includes(q) ||
          e.type?.toLowerCase().includes(q) ||
          e.country?.toLowerCase().includes(q),
      );
    }
    return result;
  }, [entities, activeCountryValue, searchQuery]);

  const entityById = useMemo(
    () => Object.fromEntries(entities.map((e) => [e.id, e])),
    [entities],
  );

  /**
   * Toggles an entity's selected state. When deselecting, also removes that
   * entity's dataset tree selections so stale choices don't persist.
   */
  const handleSelectEntity = useCallback(
    (entityId: string, selected: boolean) => {
      setSelectedEntityIds((prev) => {
        const next = new Set(prev);
        if (selected) {
          next.add(entityId);
        } else {
          next.delete(entityId);
          // Clean up dataset selections for the deselected entity
          setEntityTreeSelections((selections) => {
            const { [entityId]: _, ...rest } = selections;
            return rest;
          });
        }
        return next;
      });
    },
    [],
  );

  /** Updates the dataset tree checkbox state for a specific entity. */
  const handleTreeSelectionChange = useCallback(
    (entityId: string, selectionKeys: TreeSelectionKeys) => {
      setEntityTreeSelections((prev) => ({
        ...prev,
        [entityId]: selectionKeys,
      }));
    },
    [],
  );

  /**
   * Derives the merged export payload from current selections.
   *
   * Merges dataset keys across all selected entities into a single set
   * (the backend accepts one datasets array for the whole export, not
   * per-entity). Also determines whether any selected entity has Entity+,
   * which controls whether PoA/Officers datasets are allowed in the payload.
   *
   * @returns canDownload - true when at least one entity and one dataset are selected
   * @returns exportSelection - the payload shape expected by useEntityExport
   * @returns anyHasEntityPlus - passed to the export hook for backend filtering
   */
  const { canDownload, exportSelection, anyHasEntityPlus } = useMemo(() => {
    // Merge dataset keys from all selected entities into a single set
    const mergedDatasets = new Set<ExportDatasetKey>();
    for (const id of selectedEntityIds) {
      const keys = treeSelectionToDatasetKeys(entityTreeSelections[id] ?? null);
      for (const k of keys) {
        mergedDatasets.add(k);
      }
    }
    const merged = Array.from(mergedDatasets);
    // Check if any selected entity has Entity+ to gate PoA/Officers datasets
    const anyPlus = [...selectedEntityIds].some((id) =>
      hasEntityPlus(entityById[id] ?? { id, name: null, country: null }),
    );
    const datasets = filterAllowedDatasets(merged, anyPlus);
    const atLeastOneEntity = selectedEntityIds.size >= 1;
    const atLeastOneDataset = merged.length >= 1;
    return {
      canDownload: atLeastOneEntity && atLeastOneDataset,
      exportSelection: {
        entityIds: [...selectedEntityIds],
        datasets,
      },
      anyHasEntityPlus: anyPlus,
    };
  }, [selectedEntityIds, entityTreeSelections, entityById]);

  const handleDownloadClick = useCallback(() => {
    setConfirmDialogVisible(true);
  }, []);

  /** Triggers the actual API call. On success transitions to the success dialog. */
  const handleConfirmExport = useCallback(async () => {
    if (!exportSelection.entityIds.length || !exportSelection.datasets.length) {
      return;
    }
    try {
      await exportEntities(exportSelection, anyHasEntityPlus);
      setConfirmDialogVisible(false);
      setSuccessDialogVisible(true);
    } catch {
      // Error already set on hook; keep dialog open
    }
  }, [exportSelection, anyHasEntityPlus, exportEntities]);

  const handleSuccessClose = useCallback(() => {
    setSuccessDialogVisible(false);
    onClose();
  }, [onClose]);

  /**
   * Reset all modal state when it closes (GXM-1883).
   * Without this, reopening the modal after a download would still show the
   * previous entity/dataset selections.
   */
  useEffect(() => {
    if (!visible) {
      setConfirmDialogVisible(false);
      setSuccessDialogVisible(false);
      setSearchQuery("");
      setSelectedEntityIds(new Set());
      setEntityTreeSelections({});
      setActiveCountryValue("ALL");
    }
  }, [visible]);

  const header = (
    <div className="export-modal-header">
      <h2 className="export-modal-title">Download Entity Data</h2>
      <p className="export-modal-subtitle">
        Customize your download by choosing the entities and data you want to
        include in this report.
      </p>
    </div>
  );

  const activeIndex = tabs.findIndex((t) => t.value === activeCountryValue);
  const safeIndex = activeIndex >= 0 ? activeIndex : 0;

  return (
    <Dialog
      visible={visible}
      onHide={onClose}
      header={header}
      className="export-entity-data-modal"
      closable
      dismissableMask
      blockScroll
      style={{ width: "90vw", maxWidth: "800px" }}
    >
      <div className="export-modal-body">
        <p className="export-modal-instruction">
          Select the entities and data to include?
        </p>
        {tabMode === "multiple" && (
          <TabView
            activeIndex={safeIndex}
            onTabChange={(e) =>
              setActiveCountryValue(tabs[e.index]?.value ?? "ALL")
            }
            scrollable
            className="export-modal-tabs"
          >
            {tabs.map((t) => (
              <TabPanel key={t.value} header={t.label} />
            ))}
          </TabView>
        )}
        <div className="export-modal-search">
          <div className="export-modal-search__wrapper">
            <i className="pi pi-search export-modal-search__icon-left" />
            <InputText
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search entity by name, type or country"
              className="export-modal-search__input"
            />
            <i className="pi pi-search export-modal-search__icon-right" />
          </div>
        </div>
        <EntityListContent
          entities={filteredEntities}
          isLoading={isLoading}
          selectedEntityIds={selectedEntityIds}
          entityTreeSelections={entityTreeSelections}
          entityById={entityById}
          onSelectEntity={handleSelectEntity}
          onTreeSelectionChange={handleTreeSelectionChange}
        />
      </div>
      <div className="export-modal-footer">
        {error != null && (
          <p className="export-modal-error" role="alert">
            {EXPORT_ERROR_MESSAGE}
          </p>
        )}
        <CustomeButton label="Cancel" variant="outlined" onClick={onClose} />
        <CustomeButton
          label="Download as Excel"
          variant="contained"
          disabled={!canDownload || isExporting}
          loading={isExporting}
          onClick={handleDownloadClick}
        />
      </div>
      <Dialog
        visible={confirmDialogVisible}
        onHide={() => setConfirmDialogVisible(false)}
        showHeader={false}
        className="export-confirm-dialog"
        closable
        dismissableMask
        style={{ width: "90vw", maxWidth: "780px" }}
      >
        <div className="export-confirm-dialog__body">
          <div className="export-confirm-dialog__icon-wrapper">
            <div className="export-confirm-dialog__icon-container">
              <i className="pi pi-exclamation-circle export-confirm-dialog__icon" />
            </div>
          </div>
          <h3 className="export-confirm-dialog__title">
            {EXPORT_WARNING_TITLE}
          </h3>
          <p className="export-confirm-dialog__warning">
            {EXPORT_WARNING_MESSAGE}
          </p>
          <div className="export-confirm-dialog__footer">
            <CustomeButton
              label="Cancel"
              variant="outlined"
              onClick={() => setConfirmDialogVisible(false)}
              disabled={isExporting}
            />
            <CustomeButton
              label="Confirm & Download"
              variant="contained"
              loading={isExporting}
              disabled={isExporting}
              onClick={handleConfirmExport}
            />
          </div>
        </div>
      </Dialog>
      <Dialog
        visible={successDialogVisible}
        onHide={handleSuccessClose}
        showHeader={false}
        className="export-confirm-dialog"
        closable
        dismissableMask
        style={{ width: "90vw", maxWidth: "780px" }}
      >
        <div className="export-confirm-dialog__body">
          <div className="export-confirm-dialog__icon-wrapper">
            <div className="export-confirm-dialog__icon-container">
              <i className="pi pi-check-circle export-confirm-dialog__icon" />
            </div>
          </div>
          <h3 className="export-confirm-dialog__title">
            {EXPORT_SUCCESS_TITLE}
          </h3>
          <p className="export-confirm-dialog__warning">
            {EXPORT_SUCCESS_MESSAGE}
          </p>
          <div className="export-confirm-dialog__footer">
            <CustomeButton
              label="Close"
              variant="contained"
              onClick={handleSuccessClose}
            />
          </div>
        </div>
      </Dialog>
    </Dialog>
  );
}

/** Placeholder skeleton shown while entity data is loading. */
function EntityListSkeleton(): ReactElement {
  return (
    <div className="export-entity-list-skeleton">
      {[1, 2, 3].map((i) => (
        <div key={i} className="export-entity-list-skeleton__card">
          <div className="export-entity-list-skeleton__row">
            <Skeleton width="12rem" height="1.25rem" />
            <Skeleton width="4rem" height="1rem" />
          </div>
          <div className="export-entity-list-skeleton__row">
            <Skeleton width="8rem" height="1rem" />
          </div>
          <Skeleton
            width="100%"
            height="2.5rem"
            className="export-entity-list-skeleton__accordion"
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Renders the scrollable entity card list. For each entity, it determines
 * Entity Plus status and builds the appropriate dataset tree nodes, then
 * passes them to the EntityCard + DatasetChecklist composition.
 */
function EntityListContent({
  entities,
  isLoading,
  selectedEntityIds,
  entityTreeSelections,
  entityById,
  onSelectEntity,
  onTreeSelectionChange,
}: {
  entities: ExportModalEntity[];
  isLoading: boolean;
  selectedEntityIds: Set<string>;
  entityTreeSelections: Record<string, TreeSelectionKeys>;
  entityById: Record<string, ExportModalEntity>;
  onSelectEntity: (entityId: string, selected: boolean) => void;
  onTreeSelectionChange: (
    entityId: string,
    selectionKeys: TreeSelectionKeys,
  ) => void;
}): ReactElement {
  if (isLoading) {
    return (
      <div className="export-modal-entity-list export-modal-entity-list--loading export-modal-entity-list--scroll">
        <EntityListSkeleton />
      </div>
    );
  }
  if (entities.length === 0) {
    return (
      <div className="export-modal-entity-list">
        <p className="export-modal-empty">No entities in this view.</p>
      </div>
    );
  }
  return (
    <div className="export-modal-entity-list export-modal-entity-list--scroll">
      {entities.map((e) => {
        const isEntityPlus = hasEntityPlus(e);
        const treeNodes = buildDatasetTreeNodes(isEntityPlus);
        const selectionKeys = entityTreeSelections[e.id] ?? null;
        const selectedCount = countSelectedLeaves(selectionKeys, treeNodes);
        return (
          <EntityCard
            key={e.id}
            entity={e}
            selectable
            selected={selectedEntityIds.has(e.id)}
            onSelectEntity={onSelectEntity}
            selectedCount={selectedCount}
            informationAvailableContent={
              <DatasetChecklist
                selectionKeys={selectionKeys}
                onChange={(keys) => onTreeSelectionChange(e.id, keys)}
                showEntityPlusDatasets={isEntityPlus}
              />
            }
          />
        );
      })}
    </div>
  );
}

export default ExportEntityDataModal;
